'use client'

import { useEffect, useRef, useState } from 'react'
import { Box, Button, IconButton, Slide, Snackbar, useMediaQuery } from '@mui/material'
import type { SlideProps, SnackbarCloseReason } from '@mui/material'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { inter } from '@/lib/fonts'

// Snackbar reutilizable estilo "toast moderno" (tipo shadcn/sonner):
// entra desde abajo (Slide en la envoltura exterior), anclado
// abajo-derecha en escritorio y abajo-centro a ancho casi completo en móvil.
// La tarjeta interior se puede descartar con swipe lateral: sigue al
// dedo/cursor 1:1 y, pasado el umbral, sale volando por ese lado.
// El transform del swipe vive en la tarjeta interior a propósito: el Slide
// deja un `transform` inline en su hijo directo y lo pisaría.
// touch-action pan-y para no romper el scroll vertical en móvil.
// Pensado para sustituir progresivamente los Snackbar+Alert de MUI
// repartidos por el dashboard (BookingPopup, NoBookingsWidget, etc.).
export type AppSnackbarSeverity = 'success' | 'error' | 'info' | 'warning'

export interface AppSnackbarAction {
  label: string
  href?: string
  onClick?: () => void
}

interface Props {
  open: boolean
  onClose: () => void
  severity?: AppSnackbarSeverity
  title: string
  action?: AppSnackbarAction
  autoHideDuration?: number
}

const SEVERITY_ICON = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warning: AlertTriangle
} as const

const SEVERITY_COLOR = {
  success: 'var(--color-success)',
  error: 'var(--color-danger)',
  info: 'var(--color-info)',
  warning: 'var(--color-warning)'
} as const

const SEVERITY_BG = {
  success: 'var(--color-success-soft)',
  error: 'var(--color-danger-soft)',
  info: 'var(--color-info-soft)',
  warning: 'var(--color-warning-soft)'
} as const

const slideUp = (props: SlideProps) => <Slide {...props} direction='up' />

// Distancia lateral a partir de la cual el swipe descarta el aviso.
const SWIPE_DISMISS_PX = 80

const AppSnackbar = ({ open, onClose, severity = 'success', title, action, autoHideDuration = 6000 }: Props) => {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const Icon = SEVERITY_ICON[severity]

  // Estado del swipe-to-dismiss sobre la tarjeta interior.
  const [dragX, setDragX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const startXRef = useRef(0)
  const startYRef = useRef(0)
  const exitDirRef = useRef<1 | -1>(1)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Al empezar un toast nuevo el swipe debe estar limpio ANTES de pintar:
  // si el reset fuese en un useEffect, el primer frame del Slide de entrada
  // aún llevaría el translateX del swipe anterior y el aviso entraría
  // desplazado lateralmente en vez de subir limpio desde abajo.
  // (Ajuste de estado durante el render: React re-renderiza antes del commit.)
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setDragX(0)
      setIsDragging(false)
      setIsExiting(false)
    }
  }

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current)
    }
  }, [])

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return
    onClose()
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    if (isExiting) return
    startXRef.current = e.clientX
    startYRef.current = e.clientY
    // En ratón capturamos el puntero para no perder el arrastre si sale de
    // la tarjeta; en táctil hay captura implícita y además no debe
    // interferir con el scroll vertical (touch-action: pan-y en la tarjeta).
    if (e.pointerType === 'mouse') (e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (isExiting) return
    // Sin botón pulsado (ratón) no hay arrastre.
    if (e.pointerType === 'mouse' && e.buttons === 0) return
    const dx = e.clientX - startXRef.current
    const dy = e.clientY - startYRef.current
    // Si el gesto es claramente vertical, se aborta el swipe para dejar
    // que la página haga scroll con normalidad.
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
      setIsDragging(false)
      setDragX(0)
      return
    }
    if (dx !== 0) {
      setIsDragging(true)
      setDragX(dx)
    }
  }

  const endSwipe = (e: React.PointerEvent) => {
    if (!isDragging || isExiting) return
    // Se calcula con la posición final del evento (más fiable que el state,
    // que podría no haber volcado el último move).
    const finalDx = e.clientX - startXRef.current
    if (Math.abs(finalDx) > SWIPE_DISMISS_PX) {
      exitDirRef.current = finalDx > 0 ? 1 : -1
      setDragX(finalDx)
      setIsExiting(true)
      exitTimerRef.current = setTimeout(onClose, 200)
    } else {
      setIsDragging(false)
      setDragX(0)
    }
    if (e.pointerType === 'mouse') (e.target as HTMLElement).releasePointerCapture?.(e.pointerId)
  }

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={isMobile ? { vertical: 'bottom', horizontal: 'center' } : { vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={slideUp}
    >
      {/* Envoltura exterior: solo tamaño. La anima el Slide (entrada/salida
          desde abajo) sin interferir con el transform del swipe. */}
      <Box
        sx={{
          minWidth: isMobile ? 'calc(100vw - 32px)' : '320px',
          maxWidth: '420px'
        }}
      >
        <Box
          className={inter.className}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endSwipe}
          onPointerCancel={() => { setIsDragging(false); setDragX(0) }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: '12px',
            boxShadow: 'var(--shadow-lg)',
            padding: '12px 8px 12px 14px',
            // pan-y: el scroll vertical lo gestiona el navegador; solo los
            // gestos horizontales llegan al swipe-to-dismiss de arriba.
            touchAction: 'pan-y',
            userSelect: 'none',
            cursor: isDragging ? 'grabbing' : 'grab',
            transform: isExiting ? `translateX(${exitDirRef.current * 120}%)` : `translateX(${dragX}px)`,
            opacity: isExiting ? 0 : 1 - Math.min(Math.abs(dragX) / 400, 0.4),
            transition: isDragging && !isExiting ? 'none' : 'transform 0.25s cubic-bezier(0.2, 0, 0, 1), opacity 0.2s ease'
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: SEVERITY_BG[severity]
            }}
          >
            <Icon size={18} style={{ color: SEVERITY_COLOR[severity] }} />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: '0.875rem',
              fontWeight: 400,
              color: 'var(--color-text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {title}
          </Box>
          {action && (
            <Button
              size='small'
              variant='outlined'
              color='inherit'
              component={action.href ? 'a' : 'button'}
              {...(action.href
                ? { href: action.href, target: '_blank', rel: 'noopener noreferrer' }
                : { onClick: action.onClick })}
              sx={{
                flexShrink: 0,
                color: 'var(--color-primary-dark)',
                borderColor: 'var(--color-primary-200)',
                borderRadius: '8px',
                fontWeight: 600,
                fontFamily: 'inherit',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'var(--color-primary-lighter)',
                  borderColor: 'var(--color-primary-300)'
                }
              }}
            >
              {action.label}
            </Button>
          )}
          <IconButton
            size='small'
            onClick={() => onClose()}
            aria-label='Cerrar aviso'
            sx={{ flexShrink: 0, color: 'var(--color-text-muted)' }}
          >
            <X size={16} />
          </IconButton>
        </Box>
      </Box>
    </Snackbar>
  )
}

export default AppSnackbar
